import { CN_LANGUAGE, CN_REGION, GEOGRAPHY_COOKIE, REGION_REDIRECT_COOKIE } from '@/const/cookies';
import { getLangAndRegion } from '@/utils/getLocaleStatically';
import { useDialogContext } from '@lemonade-hq/cdk';
import type { Region } from '@lemonade-hq/lemonation';
import { ALL_REGIONS } from '@lemonade-hq/lemonation';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';

const REGIONS = {
  US: 'United States',
  NL: 'Netherlands',
  DE: 'Germany',
  FR: 'France',
  GB: 'United Kingdom',
} satisfies Record<Region, string>;

const PRODUCT_AS_PATH = [
  '/pet',
  '/renters',
  '/fr/renters',
  '/fr/en/renters',
  '/homeowners',
  '/fr/homeowners',
  '/fr/en/homeowners',
  '/car',
  '/life',
];

const EXTRA_AS_PATH = [
  '/faq',
  '/de/faq',
  '/fr/faq',
  '/nl/faq',
  '/uk/faq',
  '/de/en/faq',
  '/fr/en/faq',
  '/nl/en/faq',
  '/policy-two',
  '/de/police-zwei',
  '/de/en/policy-two',
  '/fr/police-2',
  '/fr/en/policy-two',
  '/nl/polis-twee',
  '/nl/en/policy-two',
  '/claims',
  '/uk/claims',
  '/de/en/claims',
  '/nl/en/claims',
  '/fr/en/claims',
  '/de/schadensabwicklung',
  '/fr/remboursement-sinistres',
  '/nl/claims',
  '/nl/en/claims',
  '/',
];

const LANGUAGE_AS_PATH = ['/uk', '/fr', '/de', '/nl', '/fr/en', '/de/en', '/nl/en'];

const SUPPORTED_AS_PATH = [...LANGUAGE_AS_PATH, ...PRODUCT_AS_PATH, ...EXTRA_AS_PATH];

export const useRegionRedirect = () => {
  const [showRegionRedirectBanner, setShowRegionRedirectBanner] = useState(false);
  const [cookies, setCookie] = useCookies();
  const router = useRouter();
  const [urlLang, urlRegion] = getLangAndRegion(router.asPath);
  const { closeDialog } = useDialogContext();

  const onSetBannerInteractedCookie = () => {
    closeDialog();
    setCookie(REGION_REDIRECT_COOKIE, 'true', {
      path: '/',
      domain: process.env.NEXT_PUBLIC_DOMAIN,
    });
    setShowRegionRedirectBanner(false);
  };

  const onRedirect = (language: string) => {
    onSetBannerInteractedCookie();
    const pathLang = language.toLowerCase();
    const params = router.asPath.split('?')[1];
    // we need to reload because of the bug US hero lotties position when client-side routing is used
    window.location.href = `${pathLang === 'us' ? '/' : `/${pathLang}`}${params ? `?${params}` : ''}`;
  };

  const onStayOnPage = () => {
    onSetBannerInteractedCookie();
  };

  const stayOnRegionPage = useMemo(() => {
    return REGIONS[urlRegion] || 'US';
  }, [cookies]);

  const defaultSelectedRegion = useMemo((): string => {
    const regionCookie = cookies[GEOGRAPHY_COOKIE] || false;
    if (!regionCookie) return '';
    const region = regionCookie.split('-')[0] ?? 'US';
    return region.toLowerCase();
  }, [cookies]);

  const stayOnPageLocale = useMemo(() => {
    return `${urlLang}-${urlRegion}`;
  }, [urlLang, urlRegion]);

  const currentPageLocale = useMemo(() => {
    const lang = cookies[CN_LANGUAGE] || 'en';
    const region = cookies[CN_REGION] || 'US';
    return `${lang}-${region}`;
  }, [urlLang, urlRegion]);

  useEffect(() => {
    const regionCookie = cookies[GEOGRAPHY_COOKIE] || false;
    const regionRedirectCookie = cookies[REGION_REDIRECT_COOKIE] || false;

    if (!regionCookie || regionRedirectCookie) return;

    const region = regionCookie.split('-')[0] as Region;

    if (!ALL_REGIONS.includes(region)) return;

    const cleanAsPath = router.asPath.split('?')[0];

    if (region !== urlRegion && SUPPORTED_AS_PATH.includes(cleanAsPath)) {
      setShowRegionRedirectBanner(true);
    }
  }, [urlRegion, router.asPath]);

  return {
    showRegionRedirectBanner,
    onRedirect,
    onStayOnPage,
    stayOnRegionPage,
    defaultSelectedRegion,
    stayOnPageLocale,
    currentPageLocale,
    onSetBannerInteractedCookie,
  };
};
